/**
 * Competition Service
 * 
 * Handles all competition-related API calls
 */

import api from './api';

const competitionService = {
  /**
   * Get all competitions
   * @param {Object} filters - Optional filters (status, category, etc.)
   * @returns {Promise<Array>} List of competitions
   */
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/competitions', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching competitions:', error);
      throw error;
    }
  },

  /**
   * Get competition by ID
   * @param {string} id - Competition ID
   * @returns {Promise<Object>} Competition details
   */
  getById: async (id) => {
    try {
      const response = await api.get(`/competitions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching competition:', error);
      throw error;
    }
  },

  /**
   * Create new competition
   * @param {Object} competitionData - Competition data
   * @returns {Promise<Object>} Created competition
   */
  create: async (competitionData) => {
    try {
      const response = await api.post('/competitions', normalizeCompetitionPayload(competitionData));
      return response.data;
    } catch (error) {
      console.error('Error creating competition:', error);
      throw error;
    }
  },

  /**
   * Update competition
   * @param {string} id - Competition ID
   * @param {Object} competitionData - Updated data
   * @returns {Promise<Object>} Updated competition
   */
  update: async (id, competitionData) => {
    try {
      const response = await api.put(`/competitions/${id}`, normalizeCompetitionPayload(competitionData));
      return response.data;
    } catch (error) {
      console.error('Error updating competition:', error);
      throw error;
    }
  },

  /**
   * Delete competition
   * @param {string} id - Competition ID
   * @returns {Promise<void>}
   */
  delete: async (id) => {
    try {
      await api.delete(`/competitions/${id}`);
    } catch (error) {
      console.error('Error deleting competition:', error);
      throw error;
    }
  },

  /**
   * Submit entry to competition
   * @param {string} competitionId - Competition ID
   * @param {Object} entryData - Entry data (videoLink, etc.)
   * @returns {Promise<Object>} Created submission
   */
  submitEntry: async (competitionId, entryData) => {
    try {
      const response = await api.post(`/competitions/${competitionId}/submit`, entryData);
      return response.data;
    } catch (error) {
      console.error('Error submitting entry:', error);
      throw error;
    }
  },

  /**
   * Get leaderboard for a competition
   * @param {string} competitionId - Competition ID
   * @param {Object} params - Optional pagination params
   * @returns {Promise<Object>} Leaderboard payload
   */
  getLeaderboard: async (competitionId, params = {}) => {
    try {
      const response = await api.get(`/competitions/${competitionId}/leaderboard`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },

  /**
   * Get all submissions for a competition
   * @param {string} competitionId - Competition ID
   * @param {Object} params - Optional filters
   * @returns {Promise<Object>} Submissions payload
   */
  getSubmissions: async (competitionId, params = {}) => {
    try {
      const response = await api.get(`/competitions/${competitionId}/submissions`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw error;
    }
  },

  /**
   * Get visibility-filtered submissions payload for competition page
   * @param {string} competitionId - Competition ID
   * @param {Object} params - Optional pagination params
   * @returns {Promise<Object>} Visible submissions payload
   */
  getVisibleSubmissions: async (competitionId, params = {}) => {
    try {
      const response = await api.get(`/competitions/${competitionId}/submissions/visible`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching visible submissions:', error);
      throw error;
    }
  },

  /**
   * Get authenticated user's submissions for a competition
   * @param {string} competitionId - Competition ID
   * @returns {Promise<Object>} User submissions payload
   */
  getMySubmissions: async (competitionId) => {
    try {
      const response = await api.get(`/competitions/${competitionId}/my-submissions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching my submissions:', error);
      throw error;
    }
  },

  /**
   * Revalidate an existing submission
   * @param {string} submissionId - Submission ID
   * @returns {Promise<Object>} Updated submission payload
   */
  revalidateSubmission: async (submissionId) => {
    try {
      const response = await api.post(`/submissions/${submissionId}/revalidate`);
      return response.data;
    } catch (error) {
      console.error('Error revalidating submission:', error);
      throw error;
    }
  }
};

function normalizeCompetitionPayload(competitionData) {
  const payload = { ...(competitionData || {}) };

  if ((!Array.isArray(payload.mediaSources) || payload.mediaSources.length === 0) && payload.youtubeLink) {
    payload.mediaSources = [
      {
        platform: 'youtube',
        url: payload.youtubeLink,
        videoId: null,
        type: 'full'
      }
    ];
    payload.primarySourceIndex = payload.primarySourceIndex ?? 0;
  }

  return payload;
}

export default competitionService;
